import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Globe, ChevronLeft, Disc3, Music2, Instagram, Facebook, Twitter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ShareButtons from '../components/ShareButtons';

import { API_URL } from '../utils/api';

export default function DJDetailPage() {
  const { id } = useParams();
  const [dj, setDJ] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDJ();
  }, [id]);

  const fetchDJ = async () => {
    try {
      const response = await fetch(`${API_URL}/api/djs/${id}`);
      if (response.ok) {
        setDJ(await response.json());
      }
    } catch (error) {
      console.error('Error fetching DJ:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (!dj) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-700 mb-4">DJ not found</h1>
          <Link to="/djs">
            <Button className="bg-violet-600 hover:bg-violet-500">Back to DJs</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dj-detail-page">
      {/* Header */}
      <div className="relative h-64 md:h-80 bg-gradient-to-br from-violet-700 to-purple-700">
        {dj.image_url && (
          <img
            src={dj.image_url}
            alt={dj.stage_name || dj.name}
            className="w-full h-full object-cover opacity-30"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
        
        <div className="absolute top-4 left-4 right-4 flex justify-between">
          <Link to="/djs">
            <Button variant="secondary" className="bg-white/90 backdrop-blur-sm" data-testid="back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <ShareButtons 
            title={dj?.stage_name || dj?.name}
            description={dj?.bio}
            type="DJ"
          />
        </div>

        {/* DJ Avatar */}
        <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 md:left-12 md:translate-x-0">
          <div className="w-32 h-32 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 border-4 border-white shadow-xl flex items-center justify-center overflow-hidden">
            {dj.image_url ? (
              <img src={dj.image_url} alt={dj.name} className="w-full h-full object-cover" />
            ) : (
              <Disc3 className="w-16 h-16 text-white/50" />
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 lg:px-12 pt-20 md:pt-8 pb-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl p-6 md:p-8 md:ml-40"
            >
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  {dj.is_featured && (
                    <Badge className="bg-violet-500 text-white mb-2">Featured DJ</Badge>
                  )}
                  <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="dj-name">
                    {dj.stage_name || dj.name}
                  </h1>
                  {dj.stage_name && (
                    <p className="text-slate-400 mt-1">{dj.name}</p>
                  )}
                  <p className="flex items-center text-slate-400 mt-2">
                    <MapPin className="w-5 h-5 mr-1" />
                    {dj.location || [dj.location_city, dj.location_state].filter(Boolean).join(', ') || 'Location not specified'}
                  </p>
                </div>
              </div>

              <p className="text-slate-600 text-lg mb-6" data-testid="dj-bio">{dj.bio}</p>

              {/* Genres */}
              {dj.genres?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-400 uppercase mb-3">Music Genres</h3>
                  <div className="flex flex-wrap gap-2">
                    {dj.genres.map((genre, i) => (
                      <Badge 
                        key={i}
                        className="bg-violet-100 text-violet-800 px-4 py-1.5"
                      >
                        {genre}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Services */}
              {dj.services?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-400 uppercase mb-3">Services Offered</h3>
                  <div className="flex flex-wrap gap-2">
                    {dj.services.map((service, i) => (
                      <Badge key={i} variant="outline" className="px-4 py-1.5">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>

            {/* Sample Mix */}
            {dj.sample_mix_url && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
              >
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <Music2 className="w-6 h-6 mr-2 text-violet-500" />
                  Sample Mix
                </h2>
                <a 
                  href={dj.sample_mix_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 font-medium"
                >
                  <Disc3 className="w-5 h-5" />
                  Listen to Sample Mix
                </a>
              </motion.div>
            )}
          </div>

          {/* Right Column - Contact */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-2xl shadow-xl p-6"
            >
              <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                Book This DJ
              </h2>
              <div className="space-y-4">
                {dj.phone && (
                  <a href={`tel:${dj.phone}`} className="flex items-center gap-3 text-slate-600 hover:text-violet-600 transition-colors" data-testid="dj-phone">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-violet-600" />
                    </div>
                    {dj.phone}
                  </a>
                )}
                {dj.email && (
                  <a href={`mailto:${dj.email}`} className="flex items-center gap-3 text-slate-600 hover:text-violet-600 transition-colors" data-testid="dj-email">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-violet-600" />
                    </div>
                    {dj.email}
                  </a>
                )}
                {dj.website && (
                  <a 
                    href={dj.website.startsWith('http') ? dj.website : `https://${dj.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-3 text-slate-600 hover:text-violet-600 transition-colors" 
                    data-testid="dj-website"
                  >
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                      <Globe className="w-5 h-5 text-violet-600" />
                    </div>
                    <span className="truncate">{dj.website.replace(/^https?:\/\//, '')}</span>
                  </a>
                )}
              </div>

              {/* Social Links */}
              {dj.social_links && Object.keys(dj.social_links).length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-400 uppercase mb-3">Follow</h3>
                  <div className="flex gap-3">
                    {dj.social_links.instagram && (
                      <a href={dj.social_links.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center hover:bg-violet-100 hover:text-violet-600 transition-colors">
                        <Instagram className="w-5 h-5" />
                      </a>
                    )}
                    {dj.social_links.facebook && (
                      <a href={dj.social_links.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center hover:bg-violet-100 hover:text-violet-600 transition-colors">
                        <Facebook className="w-5 h-5" />
                      </a>
                    )}
                    {dj.social_links.twitter && (
                      <a href={dj.social_links.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center hover:bg-violet-100 hover:text-violet-600 transition-colors">
                        <Twitter className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
